import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { appUrl } from "@/lib/domains";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Database,
  Filter,
  GaugeCircle,
  IndianRupee,
  LayoutList,
  ListChecks,
  Minus,
  Plus,
  ShieldCheck,
  Table2,
} from "lucide-react";
import { api } from "@/lib/api";
import { BrandLockup, PoweredByAdmissionHall } from "@/components/Brand";
import { DataAvailabilityBanner } from "@/components/DataAvailabilityBanner";

const FEATURES = [
  {
    icon: LayoutList,
    title: "Allotment History",
    body: "Every published allotment record, round by round, with the original raw category code preserved next to the normalized quota.",
  },
  {
    icon: Table2,
    title: "Closing Ranks",
    body: "Closing and opening ranks generated internally from normalized allotment records — never hand-typed, never guessed.",
  },
  {
    icon: IndianRupee,
    title: "Fee Structure",
    body: "Year-specific tuition and other charges per college and branch, so you compare the real cost of a seat.",
  },
  {
    icon: Database,
    title: "College Details",
    body: "City, ownership type, bed strength and the branch list actually seen in allotment data.",
  },
  {
    icon: Filter,
    title: "Dense Filtering",
    body: "Counselling body, year, round, college, branch, quota, category and fee range — combined in one table view.",
  },
  {
    icon: ListChecks,
    title: "Shortlists & Compare",
    body: "Save searches, build a watchlist, and put up to six college-branch options side by side.",
  },
];

const FAQS = [
  {
    q: "Which data is available at launch?",
    a: "Karnataka (KEA) 2025 only. MCC 2025 and other states follow next, and 2024 / 2023 are added once 5–6 states are stable. We keep the scope narrow on purpose to protect data quality.",
  },
  {
    q: "How are closing ranks calculated?",
    a: "They are derived inside NeetOne from published allotment records after category-code normalization. We never copy closing ranks from unverified lists.",
  },
  {
    q: "What do those Karnataka category codes mean?",
    a: "Codes like GMP, GMPH, MM, ME, RC1, OPN, OTH and MNG are mapped internally to readable groupings such as Merit-Private, Merit Private - Minority, Merit Open and Management. Raw codes are preserved for audit.",
  },
  {
    q: "How does the admission probability label work?",
    a: "You enter your AIR and preferred states and branches. NeetOne compares that against historical closing ranks and returns Low, Medium, High or Very High. If the historical evidence is weak, it returns no label rather than a misleading number.",
  },
  {
    q: "What does the Free plan include?",
    a: "You can browse every module but only the first 8 rows of any result set. MasterPlan removes the limit and unlocks probability, comparison and watchlists.",
  },
  {
    q: "Is this an official counselling website?",
    a: "No. NeetOne is a data product by Admission Hall Advisors. Always confirm final decisions against the official counselling authority notices.",
  },
];

const Nav = () => (
  <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/75 backdrop-blur-xl">
    <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
      <Link to="/" data-testid="brand-logo" aria-label="NeetOne home" className="flex min-w-0 items-center gap-3">
        <BrandLockup size="lg" wordmark="always" />
        <PoweredByAdmissionHall className="hidden whitespace-nowrap xl:inline" />
      </Link>
      <nav className="flex items-center gap-6 text-sm">
        <a href="#features" data-testid="nav-features" className="hidden text-slate-600 hover:text-[#1A365D] sm:inline">
          Features
        </a>
        <a href="#pricing" data-testid="nav-pricing" className="hidden text-slate-600 hover:text-[#1A365D] sm:inline">
          Pricing
        </a>
        <a href="#faq" data-testid="nav-faq" className="hidden text-slate-600 hover:text-[#1A365D] sm:inline">
          FAQ
        </a>
        <a
          href={appUrl("/login")}
          data-testid="nav-login-btn"
          className="border border-[#1A365D] bg-[#1A365D] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-[#122845]"
        >
          Login / Sign up
        </a>
      </nav>
    </div>
  </header>
);

const PreviewTable = () => {
  const rows = [
    ["Bangalore Medical College", "General Medicine", "OPN", "Merit Open", "1,842"],
    ["KIMS Hubballi", "Radio Diagnosis", "GMP", "Merit-Private", "4,109"],
    ["JJM Medical College", "Paediatrics", "MM", "Merit Private - Minority", "9,655"],
    ["Mysore Medical College", "Orthopaedics", "SC", "Govt Quota", "21,470"],
    ["KLE Belagavi", "Anaesthesiology", "MNG", "Management", "38,902"],
  ];
  return (
    <div className="overflow-x-auto border border-slate-200 bg-white shadow-[0_1px_0_0_rgba(15,23,42,0.04),0_18px_40px_-24px_rgba(15,23,42,0.35)]">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
          KEA 2025 · Round 1 · Closing Ranks
        </span>
        <span className="font-mono-num text-[11px] text-slate-400">5 / 4,318</span>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>College</th>
            <th>Branch</th>
            <th>Raw code</th>
            <th>Normalized quota</th>
            <th className="text-right">Closing rank</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[0]}>
              <td className="max-w-[190px] truncate">{r[0]}</td>
              <td>{r[1]}</td>
              <td className="font-mono-num text-slate-500">{r[2]}</td>
              <td>
                <span className="border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] text-blue-800">{r[3]}</span>
              </td>
              <td className="text-right font-mono-num">{r[4]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(0);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#FAFAFA]">
      <Nav />
      <DataAvailabilityBanner />

      {/* HERO */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-6xl gap-16 px-6 py-20 lg:grid-cols-[1.05fr_1fr] lg:py-28">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 border border-slate-300 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest text-slate-600">
              <BadgeCheck className="h-3.5 w-3.5 text-[#2563EB]" /> Karnataka KEA 2025 live
            </span>
            <h1 className="mt-6 font-display text-3xl font-extrabold leading-[1.08] tracking-tighter text-slate-900 sm:text-5xl lg:text-6xl">
              NEET PG counselling data,{" "}
              <br className="hidden sm:block" />
              <span className="text-[#1A365D]">without the guesswork.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">
              NeetOne turns raw counselling allotment files into a clean, filterable database of allotments, closing
              ranks, fees and college details — so you choose a seat on evidence, not on WhatsApp forwards.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a
                href={appUrl("/login")}
                data-testid="hero-start-btn"
                className="group flex items-center gap-2 border border-[#1A365D] bg-[#1A365D] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#122845]"
              >
                Start free
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href="#pricing"
                data-testid="hero-pricing-btn"
                className="border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 transition-colors hover:border-[#1A365D] hover:text-[#1A365D]"
              >
                See pricing
              </a>
            </div>
            <dl className="mt-14 grid max-w-lg grid-cols-3 gap-4 border-t border-slate-200 pt-6 sm:gap-6">
              {[
                ["Colleges", stats?.colleges ?? "—"],
                ["Allotment records", stats?.allotment_records ?? "—"],
                ["Closing rank rows", stats?.closing_rank_records ?? "—"],
              ].map(([k, v]) => (
                <div key={k}>
                  <dd className="font-mono-num text-2xl font-semibold text-slate-900">
                    {typeof v === "number" ? v.toLocaleString("en-IN") : v}
                  </dd>
                  <dt className="mt-1 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{k}</dt>
                </div>
              ))}
            </dl>
          </div>
          <div className="relative min-w-0 lg:pt-10">
            <div className="grain absolute -inset-6 -z-10 hidden lg:block" />
            <PreviewTable />
            <div className="mt-4 flex items-start gap-2 border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1A365D]" />
              Raw KEA codes are kept for audit; the normalized quota column is what powers filters and closing ranks.
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IT DOES */}
      <section className="border-b border-slate-200">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 py-20 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">What NeetOne does</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Counselling data arrives as messy PDFs and spreadsheets with codes nobody explains. NeetOne ingests those
              files through a strict admin pipeline — one counselling body, one year, one round at a time — validates
              them, maps every category code, and only then publishes.
            </p>
          </div>
          <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-3">
            {[
              ["Ingest", "Upload CSV/XLSX with explicit body, year, round and data type."],
              ["Normalize", "Aliases resolve college and branch names; codes map to readable quotas."],
              ["Publish", "Nothing goes live while a single validation error remains."],
            ].map(([t, b], i) => (
              <div key={t} className="bg-white p-6">
                <span className="font-mono-num text-xs text-slate-400">0{i + 1}</span>
                <h3 className="mt-2 font-display text-lg font-bold text-slate-900">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Built for decision-making</h2>
          <p className="mt-3 max-w-2xl text-base text-slate-600">
            Table-first, desktop-first, dense on purpose. Every screen is designed to be scanned, sorted and compared.
          </p>
          <div className="mt-12 grid gap-px border border-slate-200 bg-slate-200 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="group bg-white p-7 transition-colors hover:bg-slate-50">
                <f.icon className="h-5 w-5 text-[#2563EB]" strokeWidth={1.6} />
                <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBABILITY */}
      <section className="border-b border-slate-200">
        <div className="mx-auto grid max-w-6xl items-center gap-14 px-6 py-20 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              <GaugeCircle className="h-4 w-4 text-[#1A365D]" /> Conservative by design
            </span>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900">
              Admission probability that admits when it does not know
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Enter your AIR and preferred states and branches. NeetOne returns a plain label — Low, Medium, High or Very
              High — backed only by historical closing ranks. No invented percentages. When evidence is thin, you see an
              honest “insufficient data” state instead.
            </p>
          </div>
          <div className="border border-slate-200 bg-white p-6">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Sample output · AIR 12,400</p>
            <ul className="mt-4 divide-y divide-slate-100">
              {[
                ["Radio Diagnosis · KIMS Hubballi", "Low", "bg-rose-100 text-rose-800 border-rose-200"],
                ["Paediatrics · JJM Medical College", "Medium", "bg-amber-100 text-amber-800 border-amber-200"],
                ["Anaesthesiology · KLE Belagavi", "High", "bg-blue-100 text-blue-800 border-blue-200"],
                ["Gen. Medicine · Private (Mgmt)", "Very High", "bg-emerald-100 text-emerald-800 border-emerald-200"],
              ].map(([label, tag, cls]) => (
                <li key={label} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="text-slate-700">{label}</span>
                  <span className={`border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{tag}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Simple pricing</h2>
          <p className="mt-3 text-base text-slate-600">One counselling season, one price. No subscriptions to cancel.</p>
          <div className="mt-12 grid gap-px border border-slate-200 bg-slate-200 lg:grid-cols-2">
            <div className="bg-white p-8" data-testid="plan-free">
              <h3 className="font-display text-xl font-bold text-slate-900">Free</h3>
              <p className="mt-2 font-mono-num text-4xl font-semibold text-slate-900">₹0</p>
              <p className="mt-2 text-sm text-slate-500">Explore the product with limited rows.</p>
              <ul className="mt-7 space-y-2.5 text-sm text-slate-700">
                {["All four modules browsable", "First 8 rows of any result", "Basic college details", "No card required"].map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /> {f}
                  </li>
                ))}
              </ul>
              <a
                href={appUrl("/login")}
                data-testid="plan-free-cta"
                className="mt-8 block border border-slate-300 bg-white px-4 py-3 text-center text-sm font-semibold text-slate-800 hover:border-[#1A365D] hover:text-[#1A365D]"
              >
                Create free account
              </a>
            </div>
            <div className="relative bg-[#1A365D] p-8 text-white" data-testid="plan-master">
              <span className="absolute right-6 top-8 border border-white/30 bg-white/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest">
                Recommended
              </span>
              <h3 className="font-display text-xl font-bold">MasterPlan</h3>
              <div className="mt-2 flex items-baseline gap-2.5">
                <span className="font-mono-num text-4xl font-semibold">₹999</span>
                <span className="font-mono-num text-lg text-blue-200/80 line-through">₹4,999</span>
              </div>
              <span
                data-testid="pricing-discount-badge"
                className="mt-2 inline-block bg-amber-400 px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest text-[#1A365D]"
              >
                Save 80% · launch offer
              </span>
              <p className="mt-3 text-sm text-blue-100">Full access for the season.</p>
              <ul className="mt-7 space-y-2.5 text-sm text-blue-50">
                {[
                  "Unlimited rows in every module",
                  "Complete closing ranks & fee structure",
                  "Saved searches and watchlists",
                  "College and branch comparison",
                  "AIR-based probability labels",
                ].map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-200" /> {f}
                  </li>
                ))}
              </ul>
              <a
                href={appUrl("/login")}
                data-testid="plan-master-cta"
                className="mt-8 block bg-white px-4 py-3 text-center text-sm font-semibold text-[#1A365D] hover:bg-blue-50"
              >
                Get MasterPlan
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-b border-slate-200">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 py-20 lg:grid-cols-[0.6fr_1.4fr]">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">Questions</h2>
          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {FAQS.map((f, i) => (
              <div key={f.q}>
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 py-5 text-left"
                >
                  <span className="font-display text-base font-semibold text-slate-900">{f.q}</span>
                  {open === i ? (
                    <Minus className="h-4 w-4 shrink-0 text-slate-400" />
                  ) : (
                    <Plus className="h-4 w-4 shrink-0 text-slate-400" />
                  )}
                </button>
                {open === i && (
                  <p data-testid={`faq-answer-${i}`} className="pb-5 pr-8 text-sm leading-relaxed text-slate-600">
                    {f.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST + FOOTER */}
      <section className="bg-[#1A365D] py-16 text-white">
        <div className="mx-auto max-w-6xl px-6">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-200">Brand trust</p>
          <div className="mt-4 flex items-center gap-4">
            <BrandLockup size="xl" tone="light" wordmark="always" />
          </div>
          <p className="mt-2 text-[11px] font-semibold uppercase tracking-widest text-blue-200">
            Powered by Admission Hall Advisors
          </p>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-blue-100">
            Admission Hall Advisors has guided medical aspirants through counselling for years. NeetOne is that experience turned
            into software: strict ingestion, auditable normalization, and no number on screen that cannot be traced back
            to a published allotment record.
          </p>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-8 text-sm text-slate-500">
          <span>© {new Date().getFullYear()} NeetOne · Powered by Admission Hall Advisors</span>
          <div className="flex gap-6">
            <a href={appUrl("/login")} data-testid="footer-login-link" className="hover:text-[#1A365D]">
              User login
            </a>
            <a href={appUrl("/admin/login")} data-testid="footer-admin-link" className="hover:text-[#1A365D]">
              Admin
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
